/**
 * Plan 20a, Task 2.1 — Button states + press ripple hook
 *
 * Factory: createButtonStatesHook(config: UiVfxHookConfig)
 *   Returns a UiVfxHook<HTMLElement, ButtonStateOpts> with id 'button-states'.
 *
 * Supported kinds:
 *   hover        — scale 1.04 over fast (150ms) with snappy easing
 *   pressed      — translateY(2px) + shadow shrink over micro (75ms); sets data-ripple
 *   released     — reverses pressed over micro (75ms); removes data-ripple when done
 *   gamepad-focus — opacity pulse 0.7 ↔ 1.0 at 2s rate, repeat -1, yoyo; until killed
 *
 * Duration / easing tokens are read from data-vfx-* attributes on the target element
 * with hardcoded fallbacks so the hook compiles standalone without a token resolver.
 *
 * The hook is JS-logic only — it manipulates GSAP transforms + data-ripple attribute.
 * Consumer CSS (Plans 23/24a-c) styles the [data-ripple] affordance.
 *
 * NOTE — completion callbacks:
 *   The gsap-registry sentinel tween extends every timeline to VIRTUAL_DURATION=3600s.
 *   eventCallback('onComplete') on the root timeline fires at 3600s, not at the natural
 *   animation end. Use timeline.call(fn, undefined, absoluteSeconds) instead.
 *   Canonical reference: src/client/renderer/animation/hit-reaction.ts:17-18.
 */

import type { UiVfxHook, UiVfxHookConfig } from './types';
import type { GsapTimelineHandle } from '../../renderer/vfx/gsap-registry';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export interface ButtonStateOpts {
  kind: 'hover' | 'pressed' | 'released' | 'gamepad-focus';
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers — token reading
// ─────────────────────────────────────────────────────────────────────────────

/** Reads a millisecond duration from a data-vfx-* attribute; fallback in seconds. */
function readDurationMs(el: HTMLElement, attr: string, fallbackMs: number): number {
  const raw = el.dataset[attr];
  if (raw) {
    const parsed = parseFloat(raw);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return fallbackMs;
}

/** Reads an easing string from a data-vfx-* attribute; falls back to provided default. */
function readEasing(el: HTMLElement, attr: string, fallback: string): string {
  const raw = el.dataset[attr];
  return raw && raw.trim().length > 0 ? raw.trim() : fallback;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createButtonStatesHook(
  config: UiVfxHookConfig,
): UiVfxHook<HTMLElement, ButtonStateOpts> {
  const { gsapRegistry } = config;

  /** The most-recently-played handle (one at a time per hook instance). */
  let currentHandle: GsapTimelineHandle | null = null;
  /** The button that had data-ripple set (for cleanup in dispose()). */
  let rippleTarget: HTMLElement | null = null;

  /** Kill the current in-flight timeline, if any. */
  function killCurrent(): void {
    if (currentHandle) {
      currentHandle.dispose();
      currentHandle = null;
    }
  }

  function play(target: HTMLElement, opts?: ButtonStateOpts): GsapTimelineHandle {
    // Kill any prior timeline before spawning a new one.
    killCurrent();

    const kind = opts?.kind ?? 'hover';

    // ── Read timing tokens from element data-attributes (with fallbacks) ──────
    const fastMs = readDurationMs(target, 'vfxDurationFast', 150);
    const microMs = readDurationMs(target, 'vfxDurationMicro', 75);
    const snappyEase = readEasing(target, 'vfxEasingSnappy', 'power2.out');
    // idle-pulse: 2000ms = 2s half-period (from → to is one half; yoyo does the other)
    const idlePulseMs = readDurationMs(target, 'vfxDurationIdlePulse', 2000);

    const fastSec = fastMs / 1000;
    const microSec = microMs / 1000;
    const idlePulseSec = idlePulseMs / 1000;

    const handle = gsapRegistry.create(`button-states:${kind}`);
    const tl = handle.timeline;

    switch (kind) {
      case 'hover': {
        // scale 1.04 over fast (150ms) with snappy easing
        tl.to(
          target,
          {
            scale: 1.04,
            duration: fastSec,
            ease: snappyEase,
          },
          0,
        );
        break;
      }

      case 'pressed': {
        // translateY(2px) + shadow shrink over micro (75ms); sets data-ripple
        target.setAttribute('data-ripple', '');
        rippleTarget = target;

        tl.to(
          target,
          {
            y: 2,
            duration: microSec,
            ease: 'power2.out',
          },
          0,
        );
        break;
      }

      case 'released': {
        // Reverse the pressed transform over micro (75ms); remove data-ripple when done
        // Note: we remove data-ripple at the natural animation end using timeline.call()
        // (not eventCallback('onComplete') which would fire at 3600s per registry sentinel)
        tl.to(
          target,
          {
            y: 0,
            duration: microSec,
            ease: 'power2.out',
          },
          0,
        );

        // Schedule data-ripple removal at the natural animation end
        tl.call(
          () => {
            target.removeAttribute('data-ripple');
            if (rippleTarget === target) {
              rippleTarget = null;
            }
          },
          undefined,
          microSec,
        );
        break;
      }

      case 'gamepad-focus': {
        // Opacity pulse 0.7 ↔ 1.0 at 2s rate, repeat -1, yoyo: true, until killed.
        // Set repeat and yoyo on the timeline itself after creation (registry creates
        // the timeline without these; they are valid setters per GSAP API).
        tl.repeat(-1).yoyo(true);

        // Use explicit position args (not chained) to avoid the sentinel-append bug.
        // First half-period starts at 0; second at idlePulseSec/2.
        tl.to(
          target,
          {
            opacity: 0.7,
            duration: idlePulseSec / 2,
            ease: 'sine.inOut',
          },
          0,
        );
        tl.to(
          target,
          {
            opacity: 1.0,
            duration: idlePulseSec / 2,
            ease: 'sine.inOut',
          },
          idlePulseSec / 2,
        );

        break;
      }
    }

    currentHandle = handle;
    return handle;
  }

  function dispose(): void {
    // Remove data-ripple from any button that had it set during a 'pressed' play
    if (rippleTarget) {
      rippleTarget.removeAttribute('data-ripple');
      rippleTarget = null;
    }
    killCurrent();
  }

  return {
    id: 'button-states',
    play,
    dispose,
  };
}
